import SelectorItem from "components/utils/SelectorItem"
import { useElementEvent } from "hooks/misc/useElementEvent"
import { ReactNode, useCallback, useLayoutEffect, useMemo, useState } from "react"
import { elementScrollTo } from "seamless-scroll-polyfill"

export type SelectorItemData<DataType, ValueType extends string | number> = {
  value: ValueType
  /** The label that should be shown */
  label?: string | ReactNode
  /** If the item is selectable */
  disabled?: boolean
  /** The item should be marked as special */
  marked?: boolean
  /** The item is greyed out, but selectable */
  discouraged?: boolean
} & DataType

interface SelectorProps<DataType, ValueType extends string | number, Wrapper extends Array<ValueType> | ValueType> {
  items: SelectorItemData<DataType, ValueType>[]
  /** Value can be either a single value or an array of values.
   * A single value only makes sense if multi and range are false.
   */
  value: Wrapper
  onChange: (newValue: Wrapper) => void
  /** If this is set, the selector spans multiple lines */
  itemsPerLine?: number
  /** Can select multiple values if true */
  multi?: boolean
  /** Can select ranges if true.
   * value has to be an array in the form [startValue, endValue] */
  range?: boolean
  /** If range is true, and the start value is not in items it is
   * assumed to be this many elements after the available items.
   * If the number is negative, it is assumed to be this many elements before the first item
   * If the value is falsy, it is assumed to be 1 before */
  rangeStartElementDistance?: number
  /** If range is true, and the end value is not in items it is
   * assumed to be this many elements after the available items.
   * If the number is negative, it is assumed to be this many elements before the first item
   * If the value is falsy, it is assumed to be 1 after */
  rangeEndElementDistance?: number
}

const Selector = <DataType, ValueType extends string | number, Wrapper extends Array<ValueType> | ValueType>({
  items,
  value,
  onChange,
  multi,
  range,
  itemsPerLine,
  rangeStartElementDistance: rangeStart,
  rangeEndElementDistance: rangeEnd,
}: SelectorProps<DataType, ValueType, Wrapper>) => {
  const valueArray = useMemo(() => (typeof value === "object" ? value : [value]) as ValueType[], [value])
  const onChangeArray = useMemo(
    () =>
      (typeof value === "object" ? onChange : (v: ValueType[]) => onChange(v[0] as Wrapper)) as (
        newValue: ValueType[]
      ) => void,
    [value, onChange]
  )

  const [leftFade, setLeftFade] = useState(false)
  const [rightFade, setRightFade] = useState(false)
  const [scrollRefNode, setScrollRefNode] = useState<HTMLDivElement | null>(null)

  const setScrollRef = useCallback((node: HTMLDivElement | null) => setScrollRefNode(node), [])

  const realStartIndex = items.findIndex(item => item.value === valueArray[0])
  const realEndIndex = items.findIndex(item => item.value === valueArray[1])
  const fallbackStartIndex = rangeStart ? (rangeStart < 0 ? rangeStart : rangeStart + (items.length - 1)) : -1
  const fallbackEndIndex = rangeEnd ? (rangeEnd < 0 ? rangeEnd : rangeEnd + (items.length - 1)) : items.length
  const startIndex = realStartIndex !== -1 ? realStartIndex : fallbackStartIndex
  const endIndex = realEndIndex !== -1 ? realEndIndex : fallbackEndIndex
  const valueIndices = [startIndex, endIndex]

  const betweenValues =
    range && valueArray.length === 2
      ? items.slice(Math.max(valueIndices[0] + 1, 0), Math.max(valueIndices[1], 0)).map(item => item.value)
      : []

  // Horizontal scrolling, if itemsPerLine === 0
  const onWheel = useCallback(
    (event: HTMLElementEventMap["wheel"]) => {
      if (!itemsPerLine) {
        event.preventDefault()
        scrollRefNode && (scrollRefNode.scrollLeft += event.deltaY + event.deltaX)
      }
    },
    [itemsPerLine, scrollRefNode]
  )
  useElementEvent(scrollRefNode, "wheel", onWheel, [], { passive: false })

  const handleChange = (v: ValueType) => {
    const isInValue = valueArray.includes(v)
    if (range) {
      if (isInValue) {
        scrollToValue(v)
        onChangeArray([v, v])
        return
      }
      const newValueIndex = items.findIndex(item => item.value === v)
      if (newValueIndex === -1) {
        return
      }

      const distanceToStart = Math.abs(newValueIndex - valueIndices[0])
      const distanceToEnd = Math.abs(newValueIndex - valueIndices[1])
      const replaceStart = distanceToStart <= distanceToEnd
      const newValue = replaceStart
        ? valueIndices[1] >= newValueIndex
          ? [v, valueArray[1]]
          : [valueArray[1], v]
        : valueIndices[0] >= newValueIndex
          ? [v, valueArray[0]]
          : [valueArray[0], v]
      scrollToValue(v)
      onChangeArray(newValue)
      return
    }
    const newValue = multi ? (isInValue ? valueArray.filter(x => x !== v) : [...valueArray, v]) : [v]
    scrollToValue(newValue.slice(-1)[0])
    onChangeArray(newValue)
  }

  const scrollToValue = (currentValue: ValueType, instant = false) => {
    const itemIndex = items.findIndex(item => item.value === currentValue)
    const element = currentValue && (scrollRefNode?.children[itemIndex] as HTMLElement | undefined)

    if (!element || !scrollRefNode) {
      return
    }
    if (instant) {
      scrollRefNode.scrollLeft = element.offsetLeft - scrollRefNode.clientWidth / 2 + element.clientWidth / 2
      return
    }
    elementScrollTo(
      scrollRefNode,
      {
        left: element.offsetLeft - scrollRefNode.clientWidth / 2 + element.clientWidth / 2,
        behavior: "smooth",
      },
      { duration: 250 }
    )
  }

  useLayoutEffect(() => {
    if (!itemsPerLine && scrollRefNode && scrollRefNode.scrollWidth > scrollRefNode.clientWidth) {
      const item =
        items.find(item => (typeof value !== "object" ? item.value === value : value.find(v => v === item.value))) ||
        items.find(item => !item.disabled)

      item && scrollToValue(item.value, true)
    }
  }, [itemsPerLine, scrollRefNode])

  return (
    <>
      <style jsx>{`
        .selector {
          display: ${itemsPerLine ? "grid" : "flex"};
          grid-template-columns: repeat(${itemsPerLine}, minmax(min-content, 1fr));
          overflow-x: visible;
          overflow-y: hidden;

          -ms-overflow-style: none;
          scrollbar-width: none;
          width: 100%;
          padding: 4px;
        }
        .selector::-webkit-scrollbar {
          display: none;
        }
        /* TODO: The wrapper is only needed for anchoring the two gradients without scrolling. Ask marcel for a better way. */
        .selectorWrapper {
          position: relative;
          z-index: 0;
          overflow-x: auto;
          overflow-y: hidden;
        }
        .selectorWrapper::before,
        .selectorWrapper::after {
          content: "";
          position: absolute;
          z-index: 1;
          top: 0;
          bottom: 0;
          width: min(calc(50% - 40px), 60px);
          background: linear-gradient(90deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.0001));
          pointer-events: none;
          transition: all ease 0.25s;
        }
        .selectorWrapper::before {
          left: 0;
          ${!leftFade || itemsPerLine ? "width: 0;" : ""}
        }
        .selectorWrapper::after {
          right: 0;
          transform: rotate(180deg);
          ${!rightFade || itemsPerLine ? "width: 0;" : ""}
        }
      `}</style>
      <div className="selectorWrapper">
        <div
          ref={setScrollRef}
          className="selector"
          onScroll={e => {
            const width = e.currentTarget.clientWidth || 0
            const left = e.currentTarget.scrollLeft || 0
            const scrollWidth = e.currentTarget.scrollWidth || 0
            setLeftFade(left >= 5)
            setRightFade(left + width < scrollWidth)
          }}
        >
          {items.map((item, index) => {
            const thisValue = item.value
            const prevValue = items[index - 1]?.value as ValueType
            const nextValue = items[index + 1]?.value as ValueType
            const selected = valueArray.includes(thisValue)
            const prevSelected = valueArray.includes(prevValue) || betweenValues.includes(prevValue)
            const nextSelected = valueArray.includes(nextValue) || betweenValues.includes(nextValue)
            const inRange = betweenValues.includes(thisValue)
            return (
              <SelectorItem
                key={thisValue}
                {...{
                  selected,
                  inRange,
                  data: item,
                  prevSelected,
                  nextSelected,
                  onChange: handleChange,
                  index,
                  items: items,
                  values: valueArray,
                }}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}

export default Selector
